/**
 * Crew van een draaidag: gekoppelde Personen-records (Airtable 'Naam (from Crew)') samengevoegd met de namen
 * uit de titel van de Outlook-afspraak.
 *
 * Waarom: de Airtable-automation koppelt crew via de Outlook-genodigden. Externe crew (freelancers zonder
 * Full Frame-account, in de titel gemarkeerd met "(b)" = bevestigd of "(g)" = gevraagd) wordt niet uitgenodigd
 * en komt dus nooit in het Crew-veld. De titel "Raijmond + Paul (b) + Enya || …" is dan de enige bron voor Paul
 * (Robin, 22 sep 2026). Zonder deze samenvoeging toonde de tool "Raijmond + Enya" en leek de sync kapot.
 *
 * Niet alles vóór de scheider is een persoon (Joris, 8 okt 2026): planners zetten er ook open rollen in
 * ("+ Geluid + Visagie" = nog in te vullen) en soms een evenementnaam ("Inspiration day: Inspiration day: …").
 * Die kwamen in "Externe crew koppelen" terecht. Regels:
 * - rolwoorden (ROLWOORDEN) zijn geen persoon maar een open plek → `openRollenUitTitel` / `openRollen`;
 * - evenement-/segmenttekst (woorden als day/dag/team/training, of een stuk dat na de ":" herhaald wordt) valt weg;
 * - een naam telt alleen als hij naam-achtig is (woorden met hoofdletter, eventueel met tussenvoegsel);
 * - voor de koppellijst (`nietGekoppeld`) moet een naam óf gemarkeerd zijn ((b)/(g)/(?)), óf één voornaam
 *   (eventueel met tussenvoegsel + achternaam) die geen bekende interne medewerker is. Twijfel → weglaten:
 *   een gemiste naam is minder erg dan ruis.
 * Zelfde regels in de engine: services/planning_check.crew_uit_titel / open_rollen_uit_titel.
 */

import { INTERNE_VOORNAMEN } from './disciplines';

const SCHEIDERS = /\s*(?:\|\||\/\/|\|)\s*|\s*:\s*|\s+\/\s+/;
const PREFIX = /^(optie|studio|locatiebezoek|recce|opname|draaidag)\b\s*(\([^)]*\))?\s*:?\s*/i;
/** Tijdvak vooraan de titel: "(van 10:00 tot 12:00)" — ook gebruikt door draaidagOmschrijving. */
export const TIJDVAK = /^\(?\s*van\s+\d{1,2}[:.]\d{2}\s+tot\s+\d{1,2}[:.]\d{2}\s*\)?\s*/i;
const MARKERING = /\(\s*([a-z?])\s*\)|\b([bg])\b(?=\s*$)/i;
const STATUS = { b: 'bevestigd', g: 'gevraagd', '?': 'onzeker' };

/**
 * Rollen die planners als open plek in de titel zetten. Basis: de rol-synoniemen van de draaidagplanner
 * (engine planner_todo.ROL_SYNONIEMEN) plus wat in titels voorkomt. Kleine letters, enkele spaties.
 */
export const ROLWOORDEN = new Set([
  'geluid', 'geluidsman', 'geluidsvrouw', 'geluidstechnicus', 'sound',
  'visagie', 'visagist', 'visagiste', 'haar & make-up', 'haar en make-up', 'haar & makeup', 'make-up', 'makeup', 'mua',
  'camera', 'cameraman', 'cameravrouw', 'camera operator', 'cameraoperator', 'camera assistent', 'cameraassistent',
  'camera-assistent', 'dop', 'cinematograaf',
  'regie', 'regisseur', 'director',
  'licht', 'gaffer', 'grip', 'best boy',
  'assistent', 'productie-assistent', 'productieassistent', 'productie assistent',
  'editor', 'monteur', 'drone', 'dronepiloot', 'drone piloot',
  'fotograaf', 'fotografie', 'stylist', 'styliste', 'styling', 'food stylist', 'art director', 'setdresser',
  'productie', 'producer', 'productieleider', 'opnameleider', 'runner', 'catering',
  'acteur', 'actrice', 'acteurs', 'presentator', 'presentatrice', 'voice-over', 'model', 'figurant', 'figuranten',
  'content creator', 'contentcreator', 'crew', 'ntb', 'tbd', 'nnb', 'vacature',
]);

/**
 * Evenement-/segmenttekst, geen persoon: per woord (geen regex-\\w, zodat JS en Python hetzelfde doen).
 * Exacte woorden, woorden die met een evenementwoord beginnen ("Teamdag", "Workshops") en samenstellingen
 * op -dag/-dagen van minstens 6 letters ("Studiodag", "Heidag"). Alleen voor ongemarkeerde stukken:
 * "Dag (b)" of "Holliday (b)" blijft een persoon.
 */
const RUIS_EXACT = new Set(['day', 'days', 'dag', 'dagen', 'team', 'training', 'workshop', 'inspiratie', 'inspiration', 'uitje',
  'borrel', 'meeting', 'overleg', 'sessie', 'kantoor', 'intern', 'event']);
const RUIS_BEGIN = ['team', 'training', 'workshop', 'inspira', 'borrel', 'meeting', 'overleg', 'sessie', 'event'];
export function isRuis(tekst) {
  return String(tekst || '').toLowerCase().split(/[\s/]+/).filter(Boolean).some((w) => RUIS_EXACT.has(w)
    || RUIS_BEGIN.some((b) => w.startsWith(b))
    || (w.length >= 6 && (w.endsWith('dag') || w.endsWith('dagen'))));
}
const TUSSENVOEGSEL = new Set(['van', 'de', 'der', 'den', 'het', 'ter', 'ten', 'te', 'op', "'t", 'in', 'la', 'le', 'du', 'da', 'di', 'el', 'al']);
// Woord met hoofdletter; ook "Jan-Willem", "D'Angelo", "Łukasz" (Unicode-letters, koppelteken, apostrof).
const NAAMWOORD = /^\p{Lu}[\p{L}'’-]*$/u;

const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
const isNaam = (v) => typeof v === 'string' && v.trim() && !v.startsWith('rec');
const voornaam = (v) => String(v || '').trim().toLowerCase().split(/\s+/)[0];

/** Rolwoord? Ook "2e camera", "extra geluid", "camera 2", "Camera?" — zonder hoofdlettergevoeligheid. */
export function isRolwoord(tekst) {
  const t = norm(tekst).replace(/[?]/g, '').replace(/^(\d+e|extra|eerste|tweede)\s+/, '').replace(/\s+\d+$/, '').trim();
  return ROLWOORDEN.has(t);
}

/** Woorden met hoofdletter, eventueel met tussenvoegsels ("Jan de Vries", "Paul Caspers"). Max 4 woorden. */
function isNaamAchtig(naam) {
  const w = String(naam || '').trim().split(/\s+/);
  if (!w[0] || w.length > 4 || !NAAMWOORD.test(w[0])) return false;
  return w.slice(1).every((x) => TUSSENVOEGSEL.has(x.toLowerCase()) || NAAMWOORD.test(x));
}

/** Eén voornaam, of voornaam + tussenvoegsel(s) + achternaam ("Joep", "Jan de Vries") — niet "Paul Caspers". */
function isEnkeleNaam(naam) {
  const w = String(naam || '').trim().split(/\s+/);
  if (w.length === 1) return NAAMWOORD.test(w[0]);
  const tv = w.slice(1, -1);
  return isNaamAchtig(naam) && tv.length > 0 && tv.every((x) => TUSSENVOEGSEL.has(x.toLowerCase()));
}

/** Alle stukken vóór de scheider, ingedeeld: [{ naam, status, soort: 'persoon' | 'rol' }] (ruis valt weg). */
function stukkenUitTitel(titel) {
  let kop = String(titel || '');
  if (!kop.trim()) return [];
  kop = kop.replace(TIJDVAK, '').replace(PREFIX, '').replace(/^[\s/:|-]+/, '');
  const delen = kop.split(SCHEIDERS);
  kop = delen[0] || '';
  // zonder scheider is de hele titel vaak "Projectnaam - [nummer]" → geen crew
  if (delen.length === 1 && /\[\d+\]|\s-\s/.test(kop)) return [];
  kop = kop.replace(/^\(.*?\)\s*/, '');
  // "Haar & make-up" is één rol: niet splitsen op "&"/"en"
  kop = kop.replace(/haar\s*(?:&|\ben\b)\s*make-?up/gi, 'Haar_make-up');
  // een stuk dat ná de scheider herhaald wordt is een evenementnaam ("Inspiration day: Inspiration day")
  const verderop = new Set(delen.slice(1).map(norm).filter(Boolean));
  return kop
    .split(/\s*\+\s*|\s*,\s*|\s+en\s+|\s*&\s*/)
    .map((t) => t.trim().replace(/^Haar_make-up/i, 'Haar & make-up'))
    .filter(Boolean)
    .map((t) => {
      const m = t.match(MARKERING);
      const status = m ? STATUS[(m[1] || m[2] || '').toLowerCase()] || null : null;
      const naam = t.replace(/\(.*?\)/g, '').replace(/[?]/g, '').replace(/\s+[bg]$/i, '').trim();
      if (!naam || naam.length > 30 || /\d{3,}/.test(naam)) return null;
      if (isRolwoord(naam)) return { naam, status, soort: 'rol' };
      if (verderop.has(norm(naam)) || (!status && isRuis(naam)) || !isNaamAchtig(naam)) return null;
      return { naam, status, soort: 'persoon' };
    })
    .filter(Boolean);
}

/** Namen uit het deel vóór "||" van de titel: [{ naam, status }] — zonder rollen en evenementtekst. */
export function crewUitTitel(titel) {
  return stukkenUitTitel(titel).filter((x) => x.soort === 'persoon').map(({ naam, status }) => ({ naam, status }));
}

/** Open rollen uit de titel ("+ Geluid + Visagie" = nog in te vullen): ['Geluid', 'Visagie']. */
export function openRollenUitTitel(titel) {
  const gezien = new Set();
  const uit = [];
  stukkenUitTitel(titel).forEach((x) => {
    if (x.soort !== 'rol' || gezien.has(norm(x.naam))) return;
    gezien.add(norm(x.naam));
    uit.push(x.naam);
  });
  return uit;
}

/**
 * Gekoppelde crew + titelnamen die niet gekoppeld zijn.
 * @param {string[]|string} gekoppeldRaw  'Naam (from Crew)' (record-ids worden genegeerd)
 * @param {string} titel                  Outlook-titel (Airtable-veld Draaidag)
 * @param {{ interneNamen?: string[] }} [opties]  extra bekende interne medewerkers (voornaam of volledige naam)
 * @returns {{ gekoppeld: string[], nietGekoppeld: {naam:string,status:string|null}[],
 *   titelNietGekoppeld: {naam:string,status:string|null}[], openRollen: string[], alle: string[], label: string }}
 *   nietGekoppeld = externe crew die nog in Airtable gekoppeld moet worden ("Externe crew koppelen", ⚠);
 *   titelNietGekoppeld = alle niet-gekoppelde titelnamen, ook ongemarkeerde interne collega's (voor de crewregel).
 */
export function crewSamengevoegd(gekoppeldRaw, titel, opties = {}) {
  const gekoppeld = (Array.isArray(gekoppeldRaw) ? gekoppeldRaw : gekoppeldRaw ? [gekoppeldRaw] : []).filter(isNaam).map((s) => s.trim());
  const bekend = new Set(gekoppeld.map(voornaam));
  const intern = new Set([...INTERNE_VOORNAMEN, ...(opties.interneNamen || [])].map(voornaam));
  const titelNietGekoppeld = crewUitTitel(titel).filter((x) => !bekend.has(voornaam(x.naam)));
  const nietGekoppeld = titelNietGekoppeld.filter((x) => x.status || (isEnkeleNaam(x.naam) && !intern.has(voornaam(x.naam))));
  const extra = titelNietGekoppeld.map((x) => (x.status === 'bevestigd' ? `${x.naam} (b)` : x.status === 'gevraagd' ? `${x.naam} (g)` : x.status === 'onzeker' ? `${x.naam}?` : x.naam));
  const alle = [...gekoppeld, ...extra];
  return { gekoppeld, nietGekoppeld, titelNietGekoppeld, openRollen: openRollenUitTitel(titel), alle, label: alle.join(' + ') };
}

export default crewSamengevoegd;
